import { ReactNode, useState } from 'react';
import { format, isValid } from 'date-fns';
import { iTableFilter, iDateRange } from '../components/reusable-components/data-table/TableToolbar';
import { iFilterItem } from '../types/filterTypes';
import { describeLegacyRule, isLegacyRuleActive, matchesLegacyRule } from '../utils/legacyFilterRules';

interface iFilterDefBase {
  key: string;
  label: string;
  icon: ReactNode;
}

export interface iOptionFilterDef<T> extends iFilterDefBase {
  kind?: 'options';
  get: (row: T) => string[];
  /** Fixed buckets in this order; free lists are sorted A → Z and get a search box. */
  order?: string[];
  /** The value `isEmpty` / `isNotEmpty` rules refer to. */
  emptyLabel?: string;
  /** Turns a saved or linked rule into the options it selects, for operators the defaults don't cover. */
  fromItem?: (item: iFilterItem, labels: string[]) => string[] | null;
}

export interface iDateFilterDef<T> extends iFilterDefBase {
  kind: 'dateRange';
  /** A row with several dates (a multi-day event) matches when any of them is in the range. */
  getDate: (row: T) => Date | string | null | undefined | (Date | string | null | undefined)[];
}

export type iTableFilterDef<T> = iOptionFilterDef<T> | iDateFilterDef<T>;

const toDay = (value: Date | string | null | undefined) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? format(date, 'yyyy-MM-dd') : null;
};

const asList = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map(String)
    : value === undefined || value === null || value === ''
      ? []
      : [String(value)];

const resolveOptions = <T>(def: iOptionFilterDef<T>, items: iFilterItem[], labels: string[]): string[] => {
  const picked = new Set<string>();
  items.forEach((item) => {
    const custom = def.fromItem?.(item, labels);
    const values =
      custom ??
      (() => {
        switch (item.operator) {
          case 'isAnyOf':
          case 'is':
          case 'equals':
            return asList(item.value);
          case 'isEmpty':
            return def.emptyLabel ? [def.emptyLabel] : [];
          case 'isNotEmpty':
            return labels.filter((label) => label !== def.emptyLabel);
          default:
            return [];
        }
      })();
    values.forEach((value) => picked.add(value));
  });
  return Array.from(picked);
};

const PILL_OPERATORS = ['is', 'equals', 'isAnyOf'];
const RANGE_OPERATORS = ['is', 'onOrAfter', 'onOrBefore'];

const fitsPill = <T>(def: iTableFilterDef<T> | undefined, item: iFilterItem, labels: string[]) => {
  if (!def) return false;
  if (def.kind === 'dateRange') return RANGE_OPERATORS.includes(item.operator) && Boolean(toDay(item.value));
  if (def.fromItem?.(item, labels)) return true;
  if (PILL_OPERATORS.includes(item.operator)) return true;
  return Boolean(def.emptyLabel) && ['isEmpty', 'isNotEmpty'].includes(item.operator);
};

const resolveRange = (items: iFilterItem[]): iDateRange => {
  const range: iDateRange = {};
  items.forEach((item) => {
    const day = toDay(item.value);
    if (!day) return;
    if (item.operator === 'is') {
      range.from = day;
      range.to = day;
    } else if (item.operator === 'onOrAfter') range.from = day;
    else if (item.operator === 'onOrBefore') range.to = day;
  });
  return range;
};

const rangeItems = (key: string, range: iDateRange): iFilterItem[] => [
  ...(range.from ? [{ field: key, operator: 'onOrAfter', value: range.from }] : []),
  ...(range.to ? [{ field: key, operator: 'onOrBefore', value: range.to }] : []),
];

const groupItems = (items: iFilterItem[]) =>
  items.reduce<Record<string, iFilterItem[]>>((groups, item) => {
    (groups[item.field] ??= []).push(item);
    return groups;
  }, {});

export interface iTableFilterFields<T> {
  value: (row: T, field: string) => unknown;
  label: (field: string) => string;
}

export interface iTableFilterRule {
  id: string;
  label: string;
}

const useTableFilters = <T>(
  rows: T[],
  defs: iTableFilterDef<T>[],
  passesSearch: (row: T) => boolean,
  onChange?: () => void,
  initialItems: iFilterItem[] = [],
  fields?: iTableFilterFields<T>
) => {
  const defOf = (field: string) => defs.find((def) => def.key === field);
  const rowLabels = (def: iTableFilterDef<T> | undefined) =>
    def && def.kind !== 'dateRange' ? Array.from(new Set(rows.flatMap((row) => def.get(row)))) : [];
  const split = (items: iFilterItem[]) => {
    const pills: iFilterItem[] = [];
    const rules: iFilterItem[] = [];
    items.forEach((item) => {
      if (fitsPill(defOf(item.field), item, rowLabels(defOf(item.field)))) pills.push(item);
      else if (isLegacyRuleActive(item)) rules.push(item);
    });
    return { pills: groupItems(pills), rules };
  };
  const [initial] = useState(() => split(initialItems));
  const [itemsByKey, setItemsByKey] = useState<Record<string, iFilterItem[]>>(initial.pills);
  const [legacyRules, setLegacyRules] = useState<iFilterItem[]>(initial.rules);
  const [shownKeys, setShownKeys] = useState<string[]>([]);

  const ruleValue = (row: T, field: string) => {
    const def = defOf(field);
    if (def?.kind === 'dateRange') return def.getDate(row);
    return fields ? fields.value(row, field) : (row as Record<string, unknown>)[field];
  };
  const passesRules = (row: T) => legacyRules.every((item) => matchesLegacyRule(ruleValue(row, item.field), item));

  const labelsOf = (def: iOptionFilterDef<T>) => {
    const labels = new Set<string>();
    rows.forEach((row) => def.get(row).forEach((value) => labels.add(value)));
    if (def.emptyLabel) labels.add(def.emptyLabel);
    return labels;
  };
  const labelsByKey = Object.fromEntries(
    defs.filter((def) => def.kind !== 'dateRange').map((def) => [def.key, labelsOf(def as iOptionFilterDef<T>)])
  );

  const selectedOf = (def: iOptionFilterDef<T>) =>
    resolveOptions(def, itemsByKey[def.key] ?? [], Array.from(labelsByKey[def.key] ?? []));
  const rangeOf = (def: iDateFilterDef<T>) => resolveRange(itemsByKey[def.key] ?? []);

  const resolved = defs.map((def) =>
    def.kind === 'dateRange'
      ? { def, range: rangeOf(def), selected: [] as string[] }
      : { def, range: {} as iDateRange, selected: selectedOf(def) }
  );
  const isSet = ({ def, range, selected }: (typeof resolved)[number]) =>
    def.kind === 'dateRange' ? Boolean(range.from || range.to) : selected.length > 0;
  const applied = resolved.filter(isSet);

  /** `skip` leaves one filter out, so its own option counts are worked out against the others. */
  const passesFilters = (row: T, skip?: string) =>
    passesRules(row) &&
    applied.every(({ def, range, selected }) => {
      if (def.key === skip) return true;
      if (def.kind === 'dateRange') {
        const dates = def.getDate(row);
        return (Array.isArray(dates) ? dates : [dates]).some((date) => {
          const day = toDay(date);
          return !!day && (!range.from || day >= range.from) && (!range.to || day <= range.to);
        });
      }
      const values = def.get(row);
      return values.some((value) => selected.includes(value));
    });

  const activeKeys = Array.from(new Set([...shownKeys, ...applied.map(({ def }) => def.key)]));

  const filters: iTableFilter[] = resolved.map(({ def, range, selected }) => {
    if (def.kind === 'dateRange') {
      return {
        key: def.key,
        label: def.label,
        icon: def.icon,
        kind: 'dateRange',
        range,
        searchable: false,
        selected: [],
        options: [],
      };
    }
    const counts: Record<string, number> = Object.fromEntries(
      [...Array.from(labelsByKey[def.key] ?? []), ...selected].map((label) => [label, 0])
    );
    rows
      .filter((row) => passesSearch(row) && passesFilters(row, def.key))
      .forEach((row) => def.get(row).forEach((value) => (counts[value] = (counts[value] ?? 0) + 1)));
    const labels = def.order
      ? def.order.filter((value) => value in counts)
      : Object.keys(counts).sort((a, b) => a.localeCompare(b));
    return {
      key: def.key,
      label: def.label,
      icon: def.icon,
      searchable: !def.order,
      selected,
      options: labels.map((label) => ({ label, count: counts[label] })),
    };
  });

  const setKeyItems = (key: string, items: iFilterItem[]) => {
    setItemsByKey(({ [key]: _old, ...rest }) => (items.length ? { ...rest, [key]: items } : rest));
    onChange?.();
  };

  const toolbarProps = {
    filters,
    activeKeys,
    onAddFilter: (key: string) => setShownKeys((keys) => (keys.includes(key) ? keys : [...keys, key])),
    onRemoveFilter: (key: string) => {
      setShownKeys((keys) => keys.filter((item) => item !== key));
      setKeyItems(key, []);
    },
    onToggleOption: (key: string, value: string) => {
      const current = resolved.find(({ def }) => def.key === key)?.selected ?? [];
      const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
      setKeyItems(key, next.length ? [{ field: key, operator: 'isAnyOf', value: next }] : []);
    },
    onSetRange: (key: string, range: iDateRange) => setKeyItems(key, rangeItems(key, range)),
    onClearFilter: (key: string) => setKeyItems(key, []),
    onClearAll: () => {
      setShownKeys([]);
      setItemsByKey({});
      setLegacyRules([]);
      onChange?.();
    },
    rules: legacyRules.map(
      (item, index): iTableFilterRule => ({
        id: String(index),
        label: describeLegacyRule(item, defOf(item.field)?.label ?? fields?.label(item.field) ?? item.field),
      })
    ),
    onRemoveRule: (id: string) => {
      setLegacyRules((current) => current.filter((_item, index) => String(index) !== id));
      onChange?.();
    },
    onMenuClose: () => setShownKeys((keys) => keys.filter((key) => applied.some(({ def }) => def.key === key))),
  };

  const items: iFilterItem[] = [
    ...applied.flatMap(({ def, range, selected }) =>
      def.kind === 'dateRange' ? rangeItems(def.key, range) : [{ field: def.key, operator: 'isAnyOf', value: selected }]
    ),
    ...legacyRules,
  ];

  return {
    toolbarProps,
    items,
    setItems: (next: iFilterItem[]) => {
      const { pills, rules } = split(next);
      setShownKeys([]);
      setItemsByKey(pills);
      setLegacyRules(rules);
      onChange?.();
    },
    passesFilters: (row: T) => passesFilters(row),
    hasFilters: applied.length > 0 || legacyRules.length > 0,
    hasOpenFilters: activeKeys.length > 0,
    /**
     * Rules are held — even ones no loaded row matches yet (a dashboard link's `?source=/` before the rows arrive).
     * A server-paged list then needs every row so they can resolve.
     */
    hasRules: Object.keys(itemsByKey).length > 0 || legacyRules.length > 0,
  };
};

export default useTableFilters;
